import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Skeleton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Discount,
  ReceiptLong,
  Warning,
  LocalShipping,
  TrendingUp,
  Insights,
  Info,
  CalendarMonth,
  Close,
} from "@mui/icons-material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../auth/AuthProvider";
import API from "../services/api";

/**
 * HOME DASHBOARD (Proveedor) — Versión Reestructurada
 */

export default function HomeDashboard({ role = "compras" }) {
  const storedUserData = JSON.parse(localStorage.getItem("userData"));
  const { permissions, isSuperuser, loading: authLoading } = useAuth();

  const [dashboardData, setDashboardData] = useState({
    kpis: [],
    objectives: [],
    pending_adjustments: [],
  });
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [errorDashboard, setErrorDashboard] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().getMonth() + 1
  );
  const [trendView, setTrendView] = useState("year");
  const [showInactive, setShowInactive] = useState(true);
  const [visibleAdjustments, setVisibleAdjustments] = useState(5);

  // === Popover calendario ===
  const [anchorEl, setAnchorEl] = useState(null);
  const [calendarDay, setCalendarDay] = useState(null);

  // === Handlers calendario ===
  const handleDayClick = (anchor, cell) => {
    setAnchorEl(anchor);
    setCalendarDay(cell);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCalendarDay(null);
  };

  const COLORS = useMemo(
    () => ({
      primary: "#10173c",
      secondary: "#6F130F",
      ok: "#2e7d32",
      warn: "#ed6c02",
      danger: "#d32f2f",
      ink: "#0b132b",
    }),
    []
  );

  const KPI_ICON_MAP = useMemo(
    () => ({
      purchases_month: <TrendingUp fontSize="small" />,
      pending_invoices: <ReceiptLong fontSize="small" />,
      overdue_40_plus: <Warning fontSize="small" />,
      orders_in_transit: <LocalShipping fontSize="small" />,
      payments_this_month: <CalendarMonth fontSize="small" />,
      active_suppliers_90d: <Info fontSize="small" />,
      bills_open_amount: <Insights fontSize="small" />,
    }),
    []
  );

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Buenos días";
    if (hour < 19) return "Buenas tardes";
    return "Buenas noches";
  }, []);

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  useEffect(() => {
    if (selectedYear > currentYear) {
      setSelectedYear(currentYear);
    }
  }, [selectedYear, currentYear]);

  useEffect(() => {
    if (selectedYear === currentYear && selectedMonth > currentMonth) {
      setSelectedMonth(currentMonth);
    }
  }, [selectedYear, selectedMonth, currentYear, currentMonth]);

  // Auto-refresh al cambiar mes/año
  useEffect(() => {
    const fetchOverview = async () => {
      setLoadingDashboard(true);
      try {
        const { data } = await API.get("api/dashboard/overview/", {
          params: { year: selectedYear, month: selectedMonth },
        });
        const kpisWithIcons = (data?.kpis || []).map((k) => ({
          ...k,
          type: k.code === "payments_this_month" ? "money" : k.type,
          icon: KPI_ICON_MAP[k.code] || null,
        }));
        setDashboardData({
          kpis: kpisWithIcons,
          objectives: data?.objectives || [],
          spend_trend: data?.spend_trend || [],
          spend_trend_weekly: data?.spend_trend_weekly || [],
          spend_trend_semester: data?.spend_trend_semester || [],
          purchase_mix: data?.purchase_mix || {},
          early_payments: data?.early_payments || [],
          bills_top: data?.bills_top || [],
          logistic_agenda: data?.logistic_agenda || {},
          inactive_suppliers: data?.inactive_suppliers || [],
          calendar_events: data?.calendar_events || [],
          pending_adjustments: data?.pending_adjustments || [],
        });
        setErrorDashboard(null);
      } catch (e) {
        setErrorDashboard("No se pudo cargar el dashboard.");
        setDashboardData({
          kpis: [],
          objectives: [],
          spend_trend: [],
          spend_trend_weekly: [],
          spend_trend_semester: [],
          purchase_mix: {},
          early_payments: [],
          bills_top: [],
          logistic_agenda: {},
          inactive_suppliers: [],
          calendar_events: [],
          pending_adjustments: [],
        });
      } finally {
        setLoadingDashboard(false);
      }
    };
    fetchOverview();
  }, [KPI_ICON_MAP, selectedYear, selectedMonth]);

  // ======= Mock data =======
  const MOCK_SPEND_TREND = [
    { m: "Ene", q: 180000 },
    { m: "Feb", q: 220000 },
    { m: "Mar", q: 210000 },
    { m: "Abr", q: 260000 },
    { m: "May", q: 190000 },
    { m: "Jun", q: 235000 },
    { m: "Jul", q: 280000 },
    { m: "Ago", q: 260000 },
    { m: "Sep", q: 295000 },
    { m: "Oct", q: 315000 },
    { m: "Nov", q: 0 },
    { m: "Dic", q: 0 },
  ];
  const DEFAULT_SPEND_WEEKLY = [
    { m: "S1", q: 0 },
    { m: "S2", q: 0 },
    { m: "S3", q: 0 },
    { m: "S4", q: 0 },
    { m: "S5", q: 0 },
  ];
  const DEFAULT_SPEND_SEMESTER = [
    { m: "H1", q: 0 },
    { m: "H2", q: 0 },
  ];

  const kpis = dashboardData?.kpis || [];
  const objectives = dashboardData?.objectives || [];
  const logisticAgenda = dashboardData?.logistic_agenda || {};
  const inactiveSuppliers = dashboardData?.inactive_suppliers || [];
  const calendarEvents = dashboardData?.calendar_events || [];

  const spendTrend =
    dashboardData?.spend_trend && dashboardData.spend_trend.length
      ? dashboardData.spend_trend
      : MOCK_SPEND_TREND;
  const spendTrendWeekly =
    dashboardData?.spend_trend_weekly && dashboardData.spend_trend_weekly.length
      ? dashboardData.spend_trend_weekly
      : DEFAULT_SPEND_WEEKLY;
  const spendTrendSemester =
    dashboardData?.spend_trend_semester && dashboardData.spend_trend_semester.length
      ? dashboardData.spend_trend_semester
      : DEFAULT_SPEND_SEMESTER;
  const trendSeries =
    trendView === "month"
      ? spendTrendWeekly
      : trendView === "semester"
        ? spendTrendSemester
        : spendTrend;

  const earlyPayments = dashboardData?.early_payments || [];
  const billsTop = dashboardData?.bills_top || [];
  const purchaseMix = dashboardData?.purchase_mix || {};
  const pendingAdjustments = dashboardData?.pending_adjustments || [];
  const cashAmount = Number(purchaseMix.cash_amount || 0);
  const creditAmount = Number(purchaseMix.credit_amount || 0);
  const purchaseMixTotal = cashAmount + creditAmount;
  const purchaseMixData = [
    { name: "Contado", value: cashAmount, color: COLORS.primary },
    { name: "Credito", value: creditAmount, color: COLORS.secondary },
  ];
  const scheduledToday = logisticAgenda?.scheduled?.length ? logisticAgenda.scheduled : [];
  const delayedOrders = logisticAgenda?.delayed?.length ? logisticAgenda.delayed : [];

  const eventsByDate = useMemo(() => {
    const map = {};
    const colorBySeverity = { error: "error", warning: "warning", info: "info" };
    (calendarEvents || []).forEach((evt) => {
      const key = evt.date;
      if (!map[key]) map[key] = [];
      map[key].push({
        color: colorBySeverity[evt.severity] || "default",
        title: evt.title,
        meta: evt.meta,
        raw: evt,
      });
    });
    return map;
  }, [calendarEvents]);

  useEffect(() => {
    setVisibleAdjustments(5);
  }, [pendingAdjustments.length]);

  const visibleInactiveSuppliers = showInactive ? inactiveSuppliers : [];
  const monthOptions = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];
  const yearOptions = [currentYear - 1, currentYear];
  const maxMonth = selectedYear === currentYear ? currentMonth : 12;
  const visibleMonthOptions = monthOptions.slice(0, maxMonth);

  const calendar = buildCalendarWithEvents(
    new Date(selectedYear, selectedMonth - 1, 1),
    eventsByDate
  );

  const hasAccess =
    isSuperuser ||
    (Array.isArray(permissions) && permissions.includes("view_supplier"));

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      {/* Hero Section */}
      <HeroCompact greeting={greeting} name={storedUserData?.first_name} />

      {/* Filtros de fecha - Auto-refresh */}
      <Box sx={{
        px: 2,
        pt: 1.5,
        pb: 0.25,
        mr: 1,
        ml: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1
      }}>
        {/* Selects en línea con mejor espaciado */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: 36
        }}>
          {/* Ícono */}
          <CalendarMonth sx={{
            fontSize: 18,
            color: 'primary.main',
            opacity: 0.7
          }} />

          {/* Contenedor de selects */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'rgba(16, 23, 60, 0.02)',
            borderRadius: 1.5,
            px: 1,
            py: 0.5,
            border: '1px solid',
            borderColor: 'rgba(16, 23, 60, 0.08)'
          }}>
            {/* Año */}
            <TextField
              select
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              sx={{
                width: 75,
                '& .MuiOutlinedInput-root': {
                  height: 28,
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  color: 'primary.main'
                }
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 1.5,
                      mt: 0.5,
                      minWidth: 100
                    }
                  }
                }
              }}
            >
              {yearOptions.map((y) => (
                <MenuItem
                  key={y}
                  value={y}
                  sx={{
                    fontSize: '0.85rem',
                    py: 0.75,
                    minHeight: 36,
                    borderRadius: 0.75,
                    mx: 0.5,
                    my: 0.25
                  }}
                >
                  {y}
                </MenuItem>
              ))}
            </TextField>

            {/* Separador */}
            <Typography sx={{
              color: 'text.disabled',
              fontSize: '0.8rem',
              opacity: 0.4,
              mx: 0.25
            }}>
              /
            </Typography>

            {/* Mes */}
            <TextField
              select
              size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              sx={{
                width: 105,
                '& .MuiOutlinedInput-root': {
                  height: 28,
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiSelect-select': {
                  py: 0.5,
                  px: 1,
                  fontSize: '0.85rem',
                  fontWeight: 500
                }
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: 1.5,
                      mt: 0.5,
                      minWidth: 130
                    }
                  }
                }
              }}
            >
              {visibleMonthOptions.map((m, idx) => (
                <MenuItem
                  key={m}
                  value={idx + 1}
                  sx={{
                    fontSize: '0.85rem',
                    py: 0.75,
                    minHeight: 36,
                    borderRadius: 0.75,
                    mx: 0.5,
                    my: 0.25
                  }}
                >
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        {/* Indicador de auto-refresh */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
          <Box sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'success.main',
            opacity: 0.7,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.4)'
                },
                '70%': {
                  boxShadow: '0 0 0 6px rgba(46, 125, 50, 0)'
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(46, 125, 50, 0)'
                }
              }
            }
          }} />
          <Typography variant="caption" sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 500
          }}>
            Auto-update
          </Typography>
        </Box>
      </Box>
      {errorDashboard && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Typography variant="body2" color="error">
            {errorDashboard}
          </Typography>
        </Box>
      )}

      {!authLoading && hasAccess && (
        <Box sx={{ px: 2 }}>
          {loadingDashboard ? (
            <DashboardSkeleton />
          ) : (
            <Grid container spacing={2}>
              {/* KPIs - Fila superior */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {!kpis.length ? (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Sin KPIs disponibles.
                      </Typography>
                    </Grid>
                  ) : (
                    kpis.map((k, i) => (
                      <Grid key={k.code || i} item xs={12} sm={6} md={4} lg={3}>
                        <KpiLozenge {...k} />
                      </Grid>
                    ))
                  )}
                </Grid>
              </Grid>

              {/* Primera fila de gráficos */}
              <Grid item xs={12} lg={8}>
                <Panel
                  title="Tendencia de compras"
                  subtitle="Acumulado por mes"
                  icon={<Insights />}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    sx={{ mb: 1 }}
                  >
                    <TextField
                      select
                      size="small"
                      value={trendView}
                      onChange={(e) => setTrendView(e.target.value)}
                      sx={{
                        minWidth: 150,
                        '& .MuiOutlinedInput-root': {
                          height: 30,
                        },
                        '& .MuiSelect-select': {
                          py: 0.5,
                          px: 1,
                          fontSize: '0.75rem',
                        },
                      }}
                    >
                      <MenuItem value="year">Año (mensual)</MenuItem>
                      <MenuItem value="month">Mes (semanal)</MenuItem>
                      <MenuItem value="semester">Semestre</MenuItem>
                    </TextField>
                  </Stack>
                  <Box sx={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={trendSeries}
                        margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="gradQ" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.35} />
                            <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.05} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="m" />
                        <YAxis tickFormatter={(v) => shortMoney(v)} width={70} />
                        <RTooltip formatter={(v) => formatCurrency(v)} />
                        <Area
                          type="monotone"
                          dataKey="q"
                          stroke={COLORS.primary}
                          fill="url(#gradQ)"
                          strokeWidth={2.2}
                          animationDuration={800}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </Box>
                </Panel>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Panel
                  title="Objetivos de compra"
                  subtitle="Meta del mes y avance"
                  icon={<TrendingUp />}
                  sx={{ height: '100%' }}
                >
                  {!objectives.length ? (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      minHeight: 220
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Sin objetivos configurados.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={3} sx={{ px: 1 }}>
                      {objectives.map((o, idx) => {
                        const timePct = Math.round((Number(o.t) || 0) * 100);
                        return (
                          <Box key={idx}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="subtitle2" fontWeight={600}>
                                {o.supplier}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {timePct}% tiempo
                              </Typography>
                            </Stack>
                            <Box sx={{
                              position: 'relative',
                              height: 8,
                              bgcolor: 'divider',
                              borderRadius: 4,
                              mt: 1,
                              mb: 1
                            }}>
                              <Box sx={{
                                position: 'absolute',
                                height: '100%',
                                width: `${Math.min(100, (Number(o.v) / Number(o.metaMes)) * 100)}%`,
                                bgcolor: 'primary.main',
                                borderRadius: 4
                              }} />
                            </Box>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="caption">
                                {shortMoney(o.v)} de {shortMoney(o.metaMes)}
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {Math.round((Number(o.v) / Number(o.metaMes)) * 100)}%
                              </Typography>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </Panel>
              </Grid>

              {/* Segunda fila - Oportunidades y Agenda */}
              <Grid item xs={12} md={6}>
                <Panel
                  title="Oportunidades de pronto pago"
                  subtitle="Paga antes del corte y captura descuentos"
                  icon={<Discount />}
                >
                  {earlyPayments.length === 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay oportunidades de pronto pago activas por el momento.
                      </Typography>
                    </Box>
                  ) : (
                    <List dense>
                      {earlyPayments.map((row) => {
                        const due = row.dueIn;
                        let tone = "default";
                        if (due === 0) tone = "error";
                        else if (due <= 3) tone = "warning";

                        return (
                          <ListItem
                            key={row.id}
                            sx={{
                              mb: 1,
                              py: 1,
                              px: 1.5,
                              borderRadius: 2,
                              borderLeft: (t) =>
                                tone === "default"
                                  ? `2px solid ${t.palette.divider}`
                                  : `2px solid ${t.palette[tone].main}`,
                              bgcolor: 'background.default'
                            }}
                          >
                            <ListItemText
                              primaryTypographyProps={{
                                sx: {
                                  fontSize: "0.85rem",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                              secondaryTypographyProps={{
                                sx: { fontSize: "0.75rem", opacity: 0.8 },
                              }}
                              primary={`${row.supplier} - ${row.number}`}
                              secondary={`Saldo: ${formatCurrency(row.remaining ?? row.total)}`}
                            />

                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                size="small"
                                color={tone === "default" ? "default" : tone}
                                variant={tone === "default" ? "outlined" : "filled"}
                                label={
                                  due === 0
                                    ? "Hoy"
                                    : due === 1
                                      ? "En 1 día"
                                      : `En ${due} días`
                                }
                                sx={{ fontSize: "0.7rem", height: 24 }}
                              />
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={`${row.discount}%`}
                                sx={{ fontSize: "0.7rem", height: 24 }}
                              />
                            </Stack>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Panel>
              </Grid>

              <Grid item xs={12} md={6}>
                <Panel
                  title="Agenda logística"
                  subtitle="Hoy y retrasos"
                  icon={<LocalShipping />}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          Programadas para hoy
                        </Typography>
                        <List dense>
                          {scheduledToday.length === 0 ? (
                            <ListItem>
                              <ListItemText
                                primary="No hay órdenes programadas"
                                primaryTypographyProps={{ fontSize: '0.85rem' }}
                              />
                            </ListItem>
                          ) : (
                            scheduledToday.slice(0, 3).map((r) => (
                              <ListItem key={r.id} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={`${r.supplier} - ${r.number}`}
                                  secondary={r.date}
                                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                />
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          Órdenes retrasadas
                        </Typography>
                        <List dense>
                          {delayedOrders.length === 0 ? (
                            <ListItem>
                              <ListItemText
                                primary="No hay retrasos"
                                primaryTypographyProps={{ fontSize: '0.85rem' }}
                              />
                            </ListItem>
                          ) : (
                            delayedOrders.slice(0, 3).map((r) => (
                              <ListItem key={r.id} sx={{ py: 0.5 }}>
                                <ListItemText
                                  primary={`${r.supplier} - ${r.number}`}
                                  secondary={`Retraso: ${r.days_late} días`}
                                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                />
                                <Chip
                                  size="small"
                                  color={
                                    r.days_late >= 7
                                      ? "error"
                                      : r.days_late >= 3
                                        ? "warning"
                                        : "default"
                                  }
                                  label={`${r.days_late}d`}
                                />
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Box>
                    </Grid>
                  </Grid>
                </Panel>
              </Grid>

              {/* Tercera fila - Facturas y Calendario */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Panel
                    title="Bills pendientes (Top 5)"
                    subtitle="Deudas por cobrar con saldo"
                    icon={<Warning color="warning" />}
                  >
                    {billsTop.length === 0 ? (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          No hay bills pendientes.
                        </Typography>
                      </Box>
                    ) : (
                      <List dense>
                        {billsTop.map((row) => {
                          const original = Number(row.amount_original || 0);
                          const paid = Number(row.paid_amount || 0);
                          const remaining = Number(row.remaining_amount || 0);
                          const hasPartial = paid > 0;
                          const progress = original > 0 ? Math.min(100, (paid / original) * 100) : 0;
                          const reasonText = row.reason ? String(row.reason) : "Sin motivo";
                          return (
                            <ListItem key={row.id} sx={{ py: 0.5 }}>
                              <Stack spacing={0.6} sx={{ width: "100%" }}>
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                  <ListItemText
                                    primary={`${row.supplier || "Sin proveedor"} - ${reasonText}`}
                                    primaryTypographyProps={{
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      noWrap: true,
                                    }}
                                  />
                                  <Tooltip
                                    title={
                                      <Box>
                                        <Typography variant="caption">
                                          Original: {formatCurrency(original)}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Abonado: {formatCurrency(paid)}
                                        </Typography>
                                        <Typography variant="caption" display="block">
                                          Saldo: {formatCurrency(remaining)}
                                        </Typography>
                                      </Box>
                                    }
                                    arrow
                                  >
                                    <Chip
                                      size="small"
                                      color={hasPartial ? "success" : "warning"}
                                      variant="outlined"
                                      label={`Saldo: ${formatCurrency(remaining)}`}
                                    />
                                  </Tooltip>
                                </Stack>

                                {hasPartial && (
                                  <Typography variant="caption" color="text.secondary">
                                    Abono: {formatCurrency(paid)}
                                  </Typography>
                                )}

                                <LinearProgress
                                  variant="determinate"
                                  value={progress}
                                  color={hasPartial ? "success" : "warning"}
                                  sx={{
                                    height: 6,
                                    borderRadius: 6,
                                    bgcolor: "rgba(0,0,0,0.05)",
                                    "& .MuiLinearProgress-bar": {
                                      borderRadius: 6,
                                    },
                                  }}
                                />
                              </Stack>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Panel>
                  <PendingAdjustmentsPanel
                    items={pendingAdjustments}
                    visibleCount={visibleAdjustments}
                    onLoadMore={() => setVisibleAdjustments((prev) => Math.min(prev + 5, 10))}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Panel
                  title="Calendario operativo"
                  subtitle="Vencimientos, pagos y hitos"
                  icon={<CalendarMonth />}
                >
                  <MonthCalendar
                    data={calendar}
                    onDayClick={handleDayClick}
                  />
                  <Popover
                    open={Boolean(anchorEl) && Boolean(calendarDay)}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                    transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                    disableRestoreFocus
                    PaperProps={{
                      sx: {
                        borderRadius: 2,
                        boxShadow: 4,
                        border: (t) => `1px solid ${t.palette.divider}`,
                        bgcolor: "background.paper",
                      },
                    }}
                  >
                    <Box sx={{ p: 2, maxWidth: 360, position: "relative" }}>
                      <Box
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          zIndex: 1,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={handlePopoverClose}
                          sx={{
                            bgcolor: "rgba(0,0,0,0.04)",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.08)" },
                          }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          mb: 0.5,
                          pr: 4,
                        }}
                      >
                        {calendarDay?.label}
                      </Typography>
                      <List dense>
                        {(calendarDay?.events || []).map((e, i) => (
                          <ListItem key={i}>
                            <Chip
                              size="small"
                              sx={{ mr: 1 }}
                              color={e.color}
                              label=""
                            />
                            <ListItemText
                              primary={e.title}
                              secondary={e.meta}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Popover>
                </Panel>
              </Grid>

              <Grid item xs={12}>
                <Panel
                  title="Compras contado vs credito"
                  subtitle="Distribucion del periodo"
                  icon={<Insights />}
                >
                  {purchaseMixTotal <= 0 ? (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay datos de compras para el periodo seleccionado.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={2} alignItems="center">
                      <Box sx={{ width: "100%", maxWidth: 340, height: 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={purchaseMixData}
                              dataKey="value"
                              nameKey="name"
                              innerRadius={55}
                              outerRadius={85}
                              paddingAngle={2}
                            >
                              {purchaseMixData.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} />
                              ))}
                            </Pie>
                            <RTooltip formatter={(v, name) => [formatCurrency(v), name]} />
                          </PieChart>
                        </ResponsiveContainer>
                      </Box>

                      <Stack spacing={0.5} sx={{ width: "100%", maxWidth: 320 }}>
                        {purchaseMixData.map((row) => {
                          const pct = purchaseMixTotal
                            ? (row.value / purchaseMixTotal) * 100
                            : 0;
                          return (
                            <Stack
                              key={row.name}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: row.color,
                                  }}
                                />
                                <Typography variant="caption">{row.name}</Typography>
                              </Stack>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {formatCurrency(row.value)} ({pct.toFixed(1)}%)
                              </Typography>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Stack>
                  )}
                </Panel>
              </Grid>

              {/* Proveedores inactivos */}
              <Grid item xs={12}>
                <Panel
                  title="Proveedores inactivos"
                  subtitle="Sin OC reciente"
                  icon={<Info />}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      Sin actividad en los últimos 30 días
                    </Typography>
                    <Chip
                      size="small"
                      label={`${visibleInactiveSuppliers.length} proveedores`}
                      variant="outlined"
                    />
                  </Stack>
                  {(!visibleInactiveSuppliers || visibleInactiveSuppliers.length === 0) ? (
                    <Box sx={{ py: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No hay proveedores inactivos.
                      </Typography>
                    </Box>
                  ) : (
                    <Grid container spacing={1}>
                      {visibleInactiveSuppliers.slice(0, 6).map((s, i) => (
                        <Grid item xs={12} sm={6} md={4} key={s.id || i}>
                          <Card variant="outlined" sx={{ borderRadius: 2 }}>
                            <CardContent sx={{ py: 1.5, px: 2 }}>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar sx={{ bgcolor: 'primary.main', color: '#fff', width: 32, height: 32 }}>
                                  {(s.name || "P")[0]}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {s.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {s.days_since} días sin actividad
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Panel>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  );
}

function DashboardSkeleton() {
  return (
    <Grid container spacing={2}>
      {/* KPIs Skeleton */}
      {[1, 2, 3, 4].map((i) => (
        <Grid key={`kpi-skel-${i}`} item xs={12} sm={6} md={4} lg={3}>
          <Card variant="outlined" sx={{ borderRadius: 3 }}>
            <CardContent>
              <Skeleton width="40%" height={14} />
              <Skeleton width="75%" height={30} sx={{ mt: 1 }} />
              <Skeleton width="50%" height={18} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Gráficos Skeleton */}
      <Grid item xs={12} lg={8}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Skeleton variant="rounded" height={260} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Skeleton variant="rounded" height={260} />
          </CardContent>
        </Card>
      </Grid>

      {/* Paneles inferiores Skeleton */}
      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Skeleton variant="rounded" height={200} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Skeleton variant="rounded" height={200} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// ======= Subcomponentes (mantienen la misma implementación) =======
function HeroCompact({ greeting, name }) {
  const sunStyle = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return {
        size: 140,
        color: "rgba(255, 225, 170, 0.85)",
        blur: "22px",
        halo: "none",
      };
    }
    if (hour < 19) {
      return {
        size: 150,
        color: "rgba(255, 190, 140, 0.9)",
        blur: "24px",
        halo: "none",
      };
    }
    return {
      size: 130,
      color: "rgba(240, 244, 255, 0.96)",
      blur: "14px",
      halo: "0 0 22px rgba(140, 170, 255, 0.75)",
    };
  }, []);

  const titleGradient = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return "linear-gradient(90deg, #c56a21, #10173c)";
    }
    if (hour < 19) {
      return "linear-gradient(90deg, #10173c, #6F130F)";
    }
    return "linear-gradient(90deg, #10173c, #4b5563)";
  }, []);

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2.5, md: 3 },
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #fef4ec 0%, #f3f4ff 55%, #ffffff 100%)",
        border: "1px solid rgba(16, 23, 60, 0.08)",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: { xs: -10, md: -30 },
          right: { xs: -5, md: -25 },
          width: { xs: sunStyle.size * 0.75, md: sunStyle.size },
          height: { xs: sunStyle.size * 0.75, md: sunStyle.size },
          borderRadius: "50%",
          background: sunStyle.color,
          filter: `blur(${sunStyle.blur})`,
          boxShadow: sunStyle.halo,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ position: "relative", zIndex: 1 }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              letterSpacing: ".14em",
              color: "primary.main",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {greeting}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              background: titleGradient,
              WebkitBackgroundClip: "text",
              color: "transparent",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { xs: "100%", md: 420 },
            }}
          >
            Bienvenido, {name || "Usuario"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Información general del sistema de gestión y relación con proveedores
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function Panel({ title, subtitle, icon, children, sx = {} }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', ...sx }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 1.5 }}
        >
          {icon}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {children}
      </CardContent>
    </Card>
  );
}

function PendingAdjustmentsPanel({ items = [], visibleCount = 5, onLoadMore }) {
  const total = items.length;
  const visible = items.slice(0, visibleCount);
  const canLoadMore = total > visible.length;
  const formatShortDate = (value) => {
    if (!value) return "-";
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("es-419", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  return (
    <Panel
      title="Pendientes de ajuste/NC"
      subtitle="Notas de credito sin liquidar"
      icon={<ReceiptLong color="warning" />}
    >
      {total === 0 ? (
        <Box sx={{ py: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No hay ajustes pendientes.
          </Typography>
        </Box>
      ) : (
        <>
          <List dense>
            {visible.map((row) => {
              const supplier = row.supplier_name || row.supplier || "Sin proveedor";
              const typeLabel = row.type_label || row.type || "";
              const invoiceNumber = row.invoice_number || row.invoice || row.invoice_id || "-";
              const createdAt = row.created_at || row.createdAt || row.date || null;
              return (
                <ListItem key={row.id} sx={{ py: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                    <ListItemText
                      primary={`${supplier}${typeLabel ? ` (${typeLabel})` : ""}`}
                      secondary={`Factura #${invoiceNumber} · ${formatShortDate(createdAt)}`}
                      primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ fontSize: "0.75rem", color: "text.secondary" }}
                    />
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={formatCurrency(row.amount)}
                    />
                  </Stack>
                </ListItem>
              );
            })}
          </List>
          {canLoadMore && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button size="small" onClick={onLoadMore}>
                Cargar mas
              </Button>
            </Box>
          )}
        </>
      )}
    </Panel>
  );
}

function KpiLozenge({ label, value, hint, icon, type = "count" }) {
  const numericValue = typeof value === "number" ? value : Number(value);
  const displayValue =
    type === "money"
      ? shortMoney(numericValue)
      : Number.isFinite(numericValue)
        ? numericValue
        : value ?? "-";
  const tooltipTitle =
    type === "money" ? formatCurrency(numericValue) : hint || "";

  return (
    <Card sx={{ borderRadius: 3 }} variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: "primary.main", color: "#fff" }}>
            {icon}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              {label}
            </Typography>
            <Tooltip
              title={tooltipTitle || ""}
              placement="top"
              arrow
              disableInteractive={!tooltipTitle}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.1,
                  wordBreak: "break-word",
                }}
              >
                {displayValue}
              </Typography>
            </Tooltip>
            {hint && (
              <Typography variant="caption" color="text.secondary">
                {hint}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function MonthCalendar({ data, onDayClick }) {
  return (
    <Box sx={{ maxWidth: 420, mx: "auto", mt: 0.5 }}>
      {/* Mes y año */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          mb: 1,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 800, textTransform: "capitalize" }}
        >
          {data.monthLabel}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          Calendario mensual
        </Typography>
      </Box>

      {/* Encabezados */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          columnGap: 0.5,
          mb: 0.5,
        }}
      >
        {data.header.map((h, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              opacity: 0.7,
              textTransform: "uppercase",
              letterSpacing: ".08em",
              fontSize: "0.7rem",
            }}
          >
            {h}
          </Typography>
        ))}
      </Box>

      {/* Días */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          columnGap: 0.5,
          rowGap: 0.5,
        }}
      >
        {data.cells.map((cell, i) => {
          const hasEvents = cell.hasEvents;
          const isOtherMonth = !cell.inMonth;

          const clickProps =
            hasEvents && onDayClick
              ? {
                onClick: (e) => onDayClick(e.currentTarget, cell),
              }
              : {};

          return (
            <Box
              key={i}
              {...clickProps}
              sx={{
                height: 44,
                borderRadius: 1.5,
                position: "relative",
                px: 0.5,
                py: 0.4,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                border: (t) =>
                  `1px solid ${cell.today
                    ? t.palette.primary.light
                    : hasEvents
                      ? t.palette.divider
                      : "transparent"
                  }`,
                bgcolor: (t) =>
                  cell.today
                    ? "rgba(16,23,60,0.06)"
                    : hasEvents
                      ? "rgba(16,23,60,0.02)"
                      : t.palette.background.paper,
                opacity: isOtherMonth ? 0.35 : 1,
                transition:
                  "background-color 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, opacity 0.15s ease",
                "&:hover": hasEvents
                  ? {
                    boxShadow: 2,
                    borderColor: "primary.main",
                    bgcolor: "rgba(16,23,60,0.04)",
                  }
                  : undefined,
                cursor: hasEvents ? "pointer" : "default",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  alignSelf: "flex-start",
                  fontWeight: cell.today ? 800 : 600,
                  fontSize: "0.8rem",
                  color: isOtherMonth ? "text.disabled" : "text.primary",
                }}
              >
                {cell.day || ""}
              </Typography>

              {hasEvents && (
                <Stack
                  direction="row"
                  spacing={0.4}
                  sx={{ alignItems: "center", mt: 0.2 }}
                >
                  {(cell.events || []).slice(0, 3).map((e, j) => (
                    <Box
                      key={j}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: (t) =>
                          t.palette[e.color]?.main || t.palette.grey[500],
                      }}
                    />
                  ))}
                  {cell.events.length > 3 && (
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, fontSize: "0.65rem" }}
                    >
                      +{cell.events.length - 3}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ======= Utils (mantienen la misma implementación) =======
function shortMoney(n) {
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num)) return "-";
  if (num === 0) return "Q 0";
  const abs = Math.abs(num);
  let v = num;
  let s = "";
  if (abs >= 1_000_000_000) {
    v = num / 1_000_000_000;
    s = "B";
  } else if (abs >= 1_000_000) {
    v = num / 1_000_000;
    s = "M";
  } else if (abs >= 1_000) {
    v = num / 1_000;
    s = "K";
  }
  const decimals = Math.abs(v) < 10 ? 1 : 0;
  return `Q ${v.toFixed(decimals)}${s}`;
}

function formatCurrency(n) {
  const num = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(num)) return n;
  return new Intl.NumberFormat("es-GT", {
    style: "currency",
    currency: "GTQ",
  }).format(num);
}

function buildCalendarWithEvents(baseDate, eventsByDate = {}) {
  const today = new Date();
  const first = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
  const start = new Date(first);
  const weekday = first.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  start.setDate(first.getDate() + offset);

  const header = ["L", "M", "X", "J", "V", "S", "D"];
  const cells = [];

  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dateStr = d.toISOString().slice(0, 10);
    const events = eventsByDate[dateStr] || [];
    const inMonth = d.getMonth() === baseDate.getMonth();

    cells.push({
      day: inMonth ? d.getDate() : "",
      inMonth,
      today: d.toDateString() === today.toDateString(),
      label: inMonth
        ? d.toLocaleDateString("es-GT", {
          weekday: "long",
          day: "2-digit",
          month: "long",
        })
        : "",
      events,
      hasEvents: events.length > 0,
    });
  }

  const monthLabel = first.toLocaleDateString("es-GT", {
    month: "long",
    year: "numeric",
  });

  return { header, cells, monthLabel };
}





